import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	OnDestroy,
	output,
	signal,
	untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { normalizeString } from '@reach/utils/string/normalize-string';

import { ButtonsModule } from '~ng-common-lib/ui/buttons';
import { AutofocusOnInitDirective } from '~ng-common-lib/ui/cdk/autofocus-on-init';
import { LoaderThreeDotsComponent } from '~ng-common-lib/ui/loaders/three-dots/three-dots.component';
import { ClientReachMenuModule } from '~ng-common-lib/ui/menu';

import { TranslatorApiService } from './translator.api.service';
import {
	getSafePreferredLanguage,
	RIGHT_TO_LEFT_LANGUAGES,
	TRANSLATOR_LANGUAGES_MAP,
} from './translator.utils';

const titleCase = (str: string): string => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

@Component({
	selector: 'app-chunk-translator',
	templateUrl: './translator.component.html',
	styleUrls: ['./translator.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FormsModule,
		ButtonsModule,
		ClientReachMenuModule,
		LoaderThreeDotsComponent,
		AutofocusOnInitDirective,
	],
	host: {
		class: '',
	},
})
export class ChunkTranslatorComponent implements OnDestroy {
	private readonly translatorApi = inject(TranslatorApiService);

	public readonly $chunkInfo = input.required<{ nodeId: string; chunkId: string }>({
		alias: 'chunkInfo',
	});
	public readonly close$ = output({ alias: 'close' });

	public readonly $query = signal('');
	public readonly $translatedValue = signal<{ failed: boolean; loading: boolean; value: string }>(
		{ failed: false, loading: true, value: '' }
	);
	public readonly $selectedLanguageKey = signal(getSafePreferredLanguage());

	public readonly $allLanguages = computed(() => {
		const selected = this.$selectedLanguageKey();
		return this.allLanguages.map(({ key, value, rightToLeft, originalName }) => {
			return {
				key,
				value,
				originalName,
				selected: key === selected,
				rightToLeft,
			};
		});
	});

	public readonly $languages = computed(() => {
		const query = normalizeString(this.$query() || '');
		return this.$allLanguages().filter((lang) => {
			return (
				normalizeString(lang.value).includes(query) ||
				normalizeString(lang.originalName).includes(query)
			);
		});
	});

	public readonly $selectedLanguage = computed(() => {
		const all = this.$allLanguages();
		return (
			all.find((lang) => {
				return lang.selected;
			}) || {
				key: '??',
				value: 'Unknown',
				selected: true,
				rightToLeft: false,
				originalName: 'Unknown',
			}
		);
	});

	public readonly $showRightToLeft = computed(() => {
		const selected = this.$selectedLanguage();
		const value = this.$translatedValue();
		return selected.rightToLeft && value.value.length > 0;
	});

	private readonly allLanguages = Object.entries(TRANSLATOR_LANGUAGES_MAP)
		.map(([key, value]) => {
			const rightToLeft = RIGHT_TO_LEFT_LANGUAGES.includes(key);
			const originalName = titleCase(
				new Intl.DisplayNames([key], { type: 'language' }).of(key) || value
			);
			return { key, value, rightToLeft, originalName };
		})
		.sort((a, b) => {
			return a.originalName.localeCompare(b.originalName);
		});

	private reqId = 0;
	private _destroyed = false;

	constructor() {
		effect(() => {
			const info = this.$chunkInfo();
			const key = this.$selectedLanguageKey();

			untracked(() => {
				this.translate(info.nodeId, info.chunkId, key);
			});
		});
	}

	ngOnDestroy(): void {
		this._destroyed = true;
	}

	public selectLanguage(key: string): void {
		this.$selectedLanguageKey.set(key);
	}

	public close(): void {
		this.close$.emit();
	}

	protected onMenuClosed(): void {
		this.$query.set('');
	}

	private async translate(nodeId: string, chunkId: string, language: string): Promise<void> {
		const id = ++this.reqId;
		try {
			this.$translatedValue.set({ failed: false, loading: true, value: '' });
			const response = await this.translatorApi.translateNodeChunk(nodeId, chunkId, language);
			if (!this._destroyed && id === this.reqId) {
				this.$translatedValue.set({
					failed: false,
					loading: false,
					value: (response.translation || '').trim(),
				});
			}
		} catch (error) {
			if (!this._destroyed && id === this.reqId) {
				console.error('Translation failed:', error);
				this.$translatedValue.set({ failed: true, loading: false, value: '' });
			}
		}
	}
}
